import { OrderLabel } from '@seen/core/models/orderLabel'
import type { Order } from '@seen/core/models/orders/Order'
import { cn } from '@seen/ui'
import { compact } from 'lodash-es'
import { type HTMLAttributes, type ReactNode, useMemo } from 'react'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { LabelCircles } from '#src/components/LabelCircles.tsx'
import { OrderLabelBadge } from '#src/components/order/OrderLabelBadge.tsx'
import { OrderLabelCombobox } from '#src/components/order/OrderLabelCombobox.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface OrderLabelRowProps extends HTMLAttributes<HTMLDivElement> {
  order: Order
  maxLabelsToShow?: number
}

export function OrderLabelRow({
  order,
  className,
  maxLabelsToShow = MAX_LABELS_TO_SHOW,
  ...delegated
}: OrderLabelRowProps): ReactNode {
  const isReadOnly = useIsReadOnly()
  const { data: labels } = useSearch(OrderLabel, OrderLabel.baseSearchQuery())
  const labelSet = useMemo(
    () => new Set(labels?.map((l) => l.reference.reference)),
    [labels]
  )

  const validOrderLabels = useMemo(
    () => order.labels.filter((l) => !!l && labelSet.has(l)),
    [order.labels, labelSet]
  )

  if (validOrderLabels.length === 0) {
    return null
  }

  if (isReadOnly) {
    return (
      <div
        className={cn('flex items-center gap-0.75', className)}
        {...delegated}
      >
        {validOrderLabels.map((label) => (
          <OrderLabelBadge key={label} label={label} />
        ))}
      </div>
    )
  }

  return (
    <div className={cn('flex items-center gap-0.75', className)} {...delegated}>
      {validOrderLabels.slice(0, maxLabelsToShow).map((label) => (
        <ResourceFieldForm
          key={label}
          instance={order}
          valueFn={(o) => compact(o.labels)}
          patchFn={(o, value) => {
            o.labels = compact(value)
          }}
          className="h-full"
          render={({ field: { value, onChange, ...rest } }) => (
            <OrderLabelCombobox value={value} onChange={onChange}>
              <OrderLabelBadge label={label} {...rest} />
            </OrderLabelCombobox>
          )}
        />
      ))}
      {validOrderLabels.length > maxLabelsToShow && (
        <ResourceFieldForm
          instance={order}
          valueFn={(o) => compact(o.labels)}
          patchFn={(o, value) => {
            o.labels = compact(value)
          }}
          className="h-full"
          render={({ field: { value, onChange, ...rest } }) => (
            <OrderLabelCombobox value={value} onChange={onChange}>
              <RowBadge
                icon={
                  <LabelCircles
                    labels={compact(
                      validOrderLabels.slice(maxLabelsToShow).map((l) => l)
                    )}
                  />
                }
                {...rest}
              >
                {validOrderLabels.length === maxLabelsToShow + 1 && (
                  <>{maxLabelsToShow > 0 && '+'}1 label</>
                )}
                {validOrderLabels.length > maxLabelsToShow + 1 && (
                  <>
                    {maxLabelsToShow > 0 && '+'}
                    {validOrderLabels.length - maxLabelsToShow} labels
                  </>
                )}
              </RowBadge>
            </OrderLabelCombobox>
          )}
        />
      )}
    </div>
  )
}

const MAX_LABELS_TO_SHOW = 3
